import { IDropdownOption } from '@fluentui/react';
import { Dropdown, useClassNames } from '@h2oai/ui-kit';
import { useCallback, useEffect, useState } from 'react';

import { fsApi } from '../../featurestore/api';
import {
  ActivePermission,
  CoreService,
  DerivedInformation,
  FeatureSetSearchResults,
  JoinType,
  ListFeaturesResponse,
  ProjectSearchResults,
  VersionedId,
} from '../../featurestore/gen/ai/h2o/featurestore/api/v1/CoreService.pb';
import { getDropdownOptions } from '../../featurestore/utils';
import { featureSetJoinStylesDefault, IFeatureSetJoinClassNames, IFeatureSetJoinStyles } from './FeatureSetJoin.styles';

const joinTypeOptions: IDropdownOption[] = [
  { key: JoinType.JOIN_TYPE_INNER, text: 'Inner' },
  { key: JoinType.JOIN_TYPE_LEFT, text: 'Left' },
  { key: JoinType.JOIN_TYPE_RIGHT, text: 'Right' },
  { key: JoinType.JOIN_TYPE_FULL, text: 'Full' },
  { key: JoinType.JOIN_TYPE_CROSS, text: 'Cross' },
];

interface IFeatureSetJoinProps {
  onChange: (values: DerivedInformation) => void;
}

export const FeatureSetJoin = ({ onChange }: IFeatureSetJoinProps) => {
  const [values, setValues] = useState<DerivedInformation>({});
  const [projectOptions, setProjectOptions] = useState<IDropdownOption[]>();
  const [featureSetOptions, setFeatureSetOptions] = useState<{ left: IDropdownOption[]; right: IDropdownOption[] }>({
    left: [],
    right: [],
  });
  const [featureOptions, setFeatureOptions] = useState<{ left: IDropdownOption[]; right: IDropdownOption[] }>({
    left: [],
    right: [],
  });
  const [projectIds, setProjectIds] = useState<{ left: string; right: string }>({ left: '', right: '' });
  const loadProjects = useCallback(async () => {
    const { listableProject = [] }: ProjectSearchResults = await fsApi(CoreService.SearchProjects, {
      requiredPermission: ActivePermission.ACTIVE_PERMISSION_EDITOR,
      pageSize: 1000,
    });
    setProjectOptions(getDropdownOptions(listableProject));
  }, []);
  const loadFeatureSets = useCallback(
    async (projectId?: string, isRight?: boolean) => {
      const { listableFeatureSet = [] }: FeatureSetSearchResults = await fsApi(CoreService.SearchFeatureSets, {
        projectIds: [projectId],
        pageSize: 1000,
      });
      const options = getDropdownOptions(listableFeatureSet);
      if (isRight) {
        featureSetOptions.right = options;
      } else {
        featureSetOptions.left = options;
      }
      setFeatureSetOptions({ ...featureSetOptions });
    },
    [featureSetOptions]
  );
  const loadFeatures = useCallback(
    async (featureSetId?: string, isRight?: boolean) => {
      const { features = [] }: ListFeaturesResponse = await fsApi(CoreService.ListFeatures, {
        featureSetId,
        pageSize: 1000,
      });
      const options = getDropdownOptions(features, 'name');
      if (isRight) {
        featureOptions.right = options;
      } else {
        featureOptions.left = options;
      }
      setFeatureOptions({ ...featureOptions });
    },
    [featureOptions]
  );
  const onChangeValues = (values: DerivedInformation) => {
    setValues(values);
    onChange(values);
  };
  useEffect(() => {
    if (!projectOptions) {
      loadProjects();
    }
  }, []);
  const classNames = useClassNames<IFeatureSetJoinStyles, IFeatureSetJoinClassNames>(
    'FeatureSetJoin',
    featureSetJoinStylesDefault
  );
  return (
    <div className={classNames.root} data-test="featureSetJoin">
      <Dropdown
        label="Join Type"
        placeholder="Select Join Type"
        options={joinTypeOptions}
        selectedKey={values.transformation?.join?.joinType}
        onChange={(_e, option) => {
          if (option) {
            onChangeValues({
              ...values,
              transformation: { join: { ...values.transformation?.join, joinType: option.key as JoinType } },
            });
          }
        }}
      />
      <div className={classNames.fields}>
        <div>
          <Dropdown
            label="Left Project Source"
            placeholder="Select Project"
            options={projectOptions || []}
            selectedKey={projectIds.left}
            onChange={(_e, option) => {
              if (option) {
                const left = option.key as string;
                setProjectIds({ ...projectIds, left });
                loadFeatureSets(left);
                setFeatureSetOptions({ ...featureSetOptions, left: [] });
              }
            }}
          />
          <Dropdown
            label="Left Feature Set Source"
            placeholder="Select Feature Set"
            options={featureSetOptions.left}
            selectedKey={values.featureSetIds ? values.featureSetIds[0]?.id : undefined}
            onChange={(_e, option) => {
              if (option) {
                const id = option.key as string;
                const idVersion: VersionedId = { id, majorVersion: parseInt(option.data.version) };
                const featureSetIds = values.featureSetIds || [];
                featureSetIds[0] = idVersion;
                loadFeatures(id);
                onChangeValues({ ...values, featureSetIds });
              }
            }}
          />
          <Dropdown
            label="Left Key"
            placeholder="Select key"
            options={featureOptions.left}
            selectedKey={values.transformation?.join?.leftKey}
            onChange={(_e, option) => {
              if (option) {
                onChangeValues({
                  ...values,
                  transformation: { join: { ...values.transformation?.join, leftKey: option.key as string } },
                });
              }
            }}
          />
        </div>
        <div>
          <Dropdown
            label="Right Project Source"
            placeholder="Select Project"
            options={projectOptions || []}
            selectedKey={projectIds.right}
            onChange={(_e, option) => {
              if (option) {
                const right = option.key as string;
                setProjectIds({ ...projectIds, right });
                loadFeatureSets(right, true);
                setFeatureSetOptions({ ...featureSetOptions, right: [] });
              }
            }}
          />
          <Dropdown
            label="Right Feature Set Source"
            placeholder="Select Feature Set"
            options={featureSetOptions.right}
            selectedKey={values.featureSetIds ? values.featureSetIds[1]?.id : undefined}
            onChange={(_e, option) => {
              if (option) {
                const id = option.key as string;
                const idVersion: VersionedId = { id, majorVersion: parseInt(option.data.version) };
                const featureSetIds = values.featureSetIds || [];
                featureSetIds[1] = idVersion;
                loadFeatures(id, true);
                onChangeValues({ ...values, featureSetIds });
              }
            }}
          />
          <Dropdown
            label="Right Key"
            placeholder="Select key"
            options={featureOptions.right}
            selectedKey={values.transformation?.join?.rightKey}
            onChange={(_e, option) => {
              if (option) {
                onChangeValues({
                  ...values,
                  transformation: { join: { ...values.transformation?.join, rightKey: option.key as string } },
                });
              }
            }}
          />
        </div>
      </div>
    </div>
  );
};
